import React, { useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Grid, Paper, Typography } from '@mui/material';

import { formatDateSlashWithTime } from '~/services/TimeService';

import type { ContractingCaseDetail } from '~/types/services/contracting';

import CaseDetailFields, { DetailField } from './CaseDetailFields';

interface Props {
  caseItem: ContractingCaseDetail;
}

const formatDateTime = (value: string | null) => value && formatDateSlashWithTime(value);

export default function CaseDetailsTab({ caseItem }: Props) {
  const [isAdditionalOpen, setIsAdditionalOpen] = useState(true);

  const primaryLeft: Array<DetailField> = [
    { label: 'Case Number', value: caseItem.case_number },
    { label: 'Opportunity', value: caseItem.opportunity?.name ?? null, isEditable: true },
    { label: 'Subject', value: caseItem.subject, isEditable: true },
    { label: 'Case Owner', value: caseItem.owner.name, isPerson: true },
  ];
  const primaryRight: Array<DetailField> = [
    { label: 'Status', value: caseItem.status, isEditable: true },
    { label: 'Priority', value: caseItem.priority, isEditable: true },
    { label: 'Contact Name', value: caseItem.contact?.name ?? null, isEditable: true },
    { label: 'Original Case Owner', value: caseItem.original_owner_name, isPerson: true, isEditable: true },
  ];
  const additionalLeft: Array<DetailField> = [
    { label: 'Pending Response Since', value: formatDateTime(caseItem.pending_response_since), isEditable: true },
    { label: 'Awaiting Review Since', value: formatDateTime(caseItem.awaiting_review_since), isEditable: true },
    {
      label: 'Time in Awaiting Review',
      value: caseItem.hours_in_awaiting_review === null ? null : `${caseItem.hours_in_awaiting_review} hours`,
      isEditable: true,
    },
  ];
  const additionalRight: Array<DetailField> = [
    { label: 'Case Origin', value: caseItem.origin, isEditable: true },
    { label: 'Last Follow Up On', value: formatDateTime(caseItem.last_follow_up_at), isEditable: true },
    { label: 'Parent Case', value: caseItem.parent_case?.case_number ?? null, isEditable: true },
    { label: 'Date/Time Closed', value: formatDateTime(caseItem.closed_at) },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography
        variant="h6"
        component="h2"
        sx={{ display: 'inline-block', pb: 0.5, mb: 1, borderBottom: 3, borderColor: 'primary.main' }}
      >
        Details
      </Typography>
      <Grid container columnSpacing={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <CaseDetailFields fields={primaryLeft} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <CaseDetailFields fields={primaryRight} />
        </Grid>
      </Grid>

      <Box sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" display="block">
          Description
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {caseItem.description || '-'}
        </Typography>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Button
          color="inherit"
          fullWidth
          aria-expanded={isAdditionalOpen}
          startIcon={
            <ExpandMoreIcon sx={{ transform: isAdditionalOpen ? 'none' : 'rotate(-90deg)' }} fontSize="small" />
          }
          onClick={() => setIsAdditionalOpen((current) => !current)}
          sx={{ justifyContent: 'flex-start', textTransform: 'none', bgcolor: 'action.hover', fontWeight: 'bold' }}
        >
          Additional Information
        </Button>
        {isAdditionalOpen && (
          <Grid container columnSpacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <CaseDetailFields fields={additionalLeft} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <CaseDetailFields fields={additionalRight} />
            </Grid>
          </Grid>
        )}
      </Box>
    </Paper>
  );
}
